// src/components/CashFlow.js
// Cash Flow (direct, cash basis): opening / inflow / outflow / closing per bank
// account for a period, from the bank ledger. Credit = money in, Debit = out.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, Alert, CircularProgress, IconButton
} from '@mui/material';
import { Home, Print, AccountBalanceWallet, Description } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import CompanyHeader from './CompanyHeader';
import { useCompany } from '../hooks/useCompany';
import { downloadCsv } from '../utils/csvExport';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const todayStr    = () => new Date().toISOString().split('T')[0];

const CashFlow = () => {
  const navigate = useNavigate();
  const company = useCompany();
  const [from, setFrom]       = useState(firstOfYear());
  const [to, setTo]           = useState(todayStr());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/finance/reports/cash-flow`, { headers: hdrs(), params: { from, to } });
      setData(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load cash flow'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportExcel = () => {
    if (!data) return;
    const t = data.totals;
    const rows = [
      [company?.name || 'CASH FLOW'],
      ['CASH FLOW STATEMENT (Direct, cash basis)'],
      [`Period: ${from} to ${to}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      ['Bank', 'Account', 'Opening', 'Inflow', 'Outflow', 'Net', 'Closing'],
      ...data.rows.map(r => [r.bankName, r.accountName, r.opening, r.inflow, r.outflow, r.net, r.closing]),
      [],
      ['', 'TOTALS', t.opening, t.inflow, t.outflow, t.net, t.closing],
    ];
    downloadCsv(rows, `cash_flow_${from}_to_${to}.csv`);
  };

  const t = data?.totals;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } .cf-card { box-shadow:none !important; } }`}</style>
      <Box display="flex" gap={1.5} mb={2} className="no-print" alignItems="center" flexWrap="wrap">
        <TextField type="date" label="From" value={from} size="small"
          onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" label="To" value={to} size="small"
          onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <IconButton onClick={run} disabled={loading} color="primary" sx={{ bgcolor: '#00838f15' }}>
          {loading ? <CircularProgress size={20} /> : <AccountBalanceWallet />}
        </IconButton>
        <Box flex={1} />
        <IconButton onClick={exportExcel} title="Export to Excel" disabled={!data}
          sx={{ bgcolor: 'background.paper', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}><Description /></IconButton>
        <IconButton onClick={() => window.print()} title="Print" disabled={!data}
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Print /></IconButton>
        <IconButton onClick={() => navigate('/finance')} title="Finance Home"
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card elevation={3} className="cf-card">
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <CompanyHeader title="CASH FLOW STATEMENT" subtitle={`${from} → ${to}`} />
          {data && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Bank</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Opening</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Inflow</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Outflow</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Net</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Closing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center">No bank movements</TableCell></TableRow>
                  ) : data.rows.map(r => (
                    <TableRow key={r.bankAccountNo} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                      <TableCell>{r.bankName}<Typography variant="caption" color="text.secondary" display="block">{r.accountName}</Typography></TableCell>
                      <TableCell align="right">{fmt(r.opening)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>{fmt(r.inflow)}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>{fmt(r.outflow)}</TableCell>
                      <TableCell align="right" sx={{ color: r.net >= 0 ? 'success.main' : 'error.main' }}>{fmt(r.net)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(r.closing)}</TableCell>
                    </TableRow>
                  ))}
                  {data.rows.length > 0 && (
                    <TableRow sx={{ bgcolor: 'grey.200' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>TOTALS</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(t.opening)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>{fmt(t.inflow)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>{fmt(t.outflow)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: t.net >= 0 ? 'success.main' : 'error.main' }}>{fmt(t.net)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.05rem' }}>{sym()} {fmt(t.closing)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Cash basis from the bank ledger. Opening = balance before the From date; Closing = Opening + Inflow − Outflow.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CashFlow;
